/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import { Dispatch, SetStateAction } from 'react';

import { cn } from '../../lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import Icons from '../../icons/icons';
import { MAX_EVAL_JOBS_COUNT } from '../../utils/constants';
import { CustomButton } from '../common/custom-button';

const EvalButton = ({
  title,
  tooltipDisabled,
  evaluateTooltip,
  setEvaluateOpen,
  isFactsLoading,
  runningJobsCount,
  variant = 'default',
}: {
  title: string;
  evaluateTooltip: string;
  tooltipDisabled?: string;
  setEvaluateOpen?: Dispatch<SetStateAction<boolean>>;
  isFactsLoading?: boolean;
  runningJobsCount?: number;
  variant?: 'default' | 'icon';
}) => {
  const isMaxJobsReached = runningJobsCount === MAX_EVAL_JOBS_COUNT;
  const isDisabled = isFactsLoading || isMaxJobsReached;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <CustomButton
            onClick={() => setEvaluateOpen?.(true)}
            customVariant="primary"
            customSize="regular"
            disabled={variant === 'icon' ? isDisabled : isFactsLoading}
            className={cn(
              'flex items-center gap-1 bg-transparent hover:bg-transparent',
              {
                // icon variant
                'ml-1 h-auto cursor-pointer p-0 text-gray-400 hover:text-white':
                  variant === 'icon',
                'cursor-not-allowed opacity-50':
                  variant === 'icon' && isMaxJobsReached,

                // default variant
                'ml-4 h-7 rounded-e rounded-s border-2 border-form-control-border-rest px-4 py-1.5 text-btn-label dark:text-body-secondary':
                  variant === 'default',
                'cursor-not-allowed border-form-control-border-disabled text-form-input-disabled':
                  variant === 'default' && isMaxJobsReached,
              }
            )}
          >
            {Icons.EvaluateIcon(
              cn(
                variant === 'icon'
                  ? 'size-4'
                  : 'size-4 text-icon-fill text-[#EBEFF1]',
                {
                  'opacity-50': isMaxJobsReached,
                }
              )
            )}
            {variant === 'default' && title}
          </CustomButton>
        </TooltipTrigger>

        <TooltipContent
          side="bottom"
          align="center"
          className="max-w-72 rounded border-transparent bg-fill-form-control-selected text-[11px] leading-4 tracking-[2%]"
        >
          {isMaxJobsReached ? tooltipDisabled : evaluateTooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default EvalButton;